import { useCallback, useState } from "react";
import {
  useFormVersionLagQuery,
  type AttributeReviewFieldDecisionInput,
} from "@/graphql/generated";
import {
  finishedRowChanges,
  textOf,
  type FinishedRowChange,
} from "@/lib/attributeReview/finishedRowChanges";
import type { AttributeReviewMappingRow } from "./useAttributeReview.types";

export interface FinishedEditRowChanges {
  label: string;
  changes: FinishedRowChange[];
}

export interface FinishedEditDialogState {
  rows: FinishedEditRowChanges[];
  form: {
    marketplace: string;
    productType: string;
    stamp: string;
    stamped: number;
  } | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function useFinishedEditConfirm(session: {
  editing: boolean;
  marketplace: string | null;
  productType: string | null;
}) {
  const { data: lagData, refetch: refetchLag } = useFormVersionLagQuery(
    undefined,
    {
      enabled: session.editing,
    },
  );
  const [pending, setPending] = useState<{
    rows: FinishedEditRowChanges[];
    resolve: (ok: boolean) => void;
  } | null>(null);

  const confirmFinishedEdit = useCallback(
    (
      decisions: AttributeReviewFieldDecisionInput[],
      mappings: AttributeReviewMappingRow[],
    ) =>
      new Promise<boolean>((resolve) => {
        void refetchLag();
        const rows = decisions.map((decision) => {
          const row = mappings.find(
            (m) => String(m.id) === decision.sourceFieldId,
          );
          return {
            label: textOf(row?.sourceAttribute) ?? decision.sourceFieldId,
            changes: row ? finishedRowChanges(row, decision) : [],
          };
        });
        setPending({ rows, resolve });
      }),
    [refetchLag],
  );

  const close = useCallback(
    (ok: boolean) => {
      pending?.resolve(ok);
      setPending(null);
    },
    [pending],
  );

  const form =
    (lagData?.formVersionLag ?? []).find(
      (f) =>
        f.marketplace === session.marketplace &&
        f.productType === session.productType,
    ) ?? null;

  const finishedEditDialog: FinishedEditDialogState | null = pending
    ? {
        rows: pending.rows,
        form,
        onConfirm: () => close(true),
        onCancel: () => close(false),
      }
    : null;

  return { confirmFinishedEdit, finishedEditDialog };
}
